import React, { useState, useEffect, useMemo, useRef } from 'react';
import refundsService from '../services/refundsService';
import TyroRefundModal from '../components/checkout/TyroRefundModal';
import RefundReceiptModal from '../components/checkout/RefundReceiptModal';
import { useStore } from '../context/StoreContext';
import CashierQuickActions from '../components/common/CashierQuickActions';
import { FiSearch, FiPlus, FiMinus } from 'react-icons/fi';
import { getReceiptCandidates, normalizeReceiptNumber } from '../utils/receiptUtils';
import { formatInStoreTimezone } from '../utils/dateUtils';
import useStoreTimezone from '../hooks/useStoreTimezone';

const PRICE_MATCH_EPSILON = 0.01;

const getSaleItemProductId = (item) => String(item?.product?._id || item?.product || '');

const getEffectivePricePerItem = (item, sale) => {
  const lineTotal = Number(item.price || 0) * Number(item.quantity || 0);
  const lineItemDiscount = Number(item.discount || 0);
  const totalLineItemDiscounts = (sale.items || []).reduce((acc, row) => acc + Number(row.discount || 0), 0);
  const orderLevelDiscount = Number(sale.totalDiscount || 0) - totalLineItemDiscounts;
  const orderDiscountShare = Number(sale.subtotal || 0) > 0
    ? (orderLevelDiscount * (lineTotal / Number(sale.subtotal || 1)))
    : 0;
  const totalDiscountOnItem = lineItemDiscount + orderDiscountShare;
  const effectiveLineTotal = lineTotal - totalDiscountOnItem;
  return Number(item.quantity || 0) > 0 ? effectiveLineTotal / Number(item.quantity) : 0;
};

const parseLineIndex = (rawIndex, lineCount) => {
  const parsed = Number(rawIndex);
  if (!Number.isInteger(parsed) || parsed < 0 || parsed >= lineCount) return null;
  return parsed;
};

const buildItemsToReturnPayload = (sale, selectedByLine) => {
  if (!sale?.items?.length) return [];
  return Object.entries(selectedByLine)
    .map(([rawIndex, rawQty]) => {
      const saleItemIndex = parseLineIndex(rawIndex, sale.items.length);
      const quantity = Number(rawQty);
      if (saleItemIndex === null || !Number.isFinite(quantity) || quantity <= 0) return null;
      const line = sale.items[saleItemIndex];
      return {
        productId: getSaleItemProductId(line),
        quantity,
        saleItemIndex,
      };
    })
    .filter(Boolean);
};

const roundMoney = (value) => Math.round(((Number(value) || 0) + Number.EPSILON) * 100) / 100;

const normalizeSalePaymentAllocations = (sale) => {
  if (!sale) return [];
  if (Array.isArray(sale.paymentAllocations) && sale.paymentAllocations.length > 0) {
    return sale.paymentAllocations
      .map((allocation, index) => ({
        allocationId: allocation?.allocationId || `alloc-${index + 1}`,
        method: allocation?.method || sale.paymentMethod || 'Card',
        source: allocation?.source || sale.paymentSource || 'manual',
        amount: roundMoney(allocation?.amount || 0),
      }))
      .filter((allocation) => allocation.amount > 0);
  }
  return [
    {
      allocationId: 'alloc-1',
      method: sale.paymentMethod || 'Card',
      source: sale.paymentSource || 'manual',
      amount: roundMoney(sale.finalAmount || sale.total || 0),
    },
  ];
};

const buildRefundedAmountByAllocation = (previousRefunds = []) => {
  const refunded = {};
  previousRefunds.forEach((refund) => {
    if (Array.isArray(refund?.refundAllocations) && refund.refundAllocations.length > 0) {
      refund.refundAllocations.forEach((allocation) => {
        const id = allocation?.allocationId || 'alloc-1';
        refunded[id] = roundMoney((refunded[id] || 0) + Number(allocation?.amount || 0));
      });
      return;
    }
    refunded['alloc-1'] = roundMoney((refunded['alloc-1'] || 0) + Number(refund?.refundAmount || 0));
  });
  return refunded;
};

const isTyroAllocation = (allocation) => {
  const method = String(allocation?.method || '').toLowerCase();
  const source = String(allocation?.source || '').toLowerCase();
  return method === 'tyro' || source === 'tyro';
};

const resolveTyroReceiptCopies = (receipts) => {
  if (!receipts) return [];
  if (Array.isArray(receipts.copies) && receipts.copies.length > 0) {
    return receipts.copies.filter((copy) => copy?.customerReceipt || copy?.merchantReceipt);
  }
  if (receipts.customerReceipt || receipts.merchantReceipt) {
    return [{
      customerReceipt: receipts.customerReceipt || '',
      merchantReceipt: receipts.merchantReceipt || '',
      tyroTransactionId: receipts.tyroTransactionId || undefined,
    }];
  }
  return [];
};

const mergeTyroReceipts = (previous, incoming, fallbackTyroTransactionId) => {
  const previousCopies = resolveTyroReceiptCopies(previous);
  const incomingCopies = resolveTyroReceiptCopies(
    incoming
      ? {
          ...incoming,
          tyroTransactionId: incoming.tyroTransactionId || fallbackTyroTransactionId || undefined,
        }
      : null
  );
  const combinedCopies = [...previousCopies, ...incomingCopies].map((copy) => ({
    customerReceipt: copy.customerReceipt || '',
    merchantReceipt: copy.merchantReceipt || '',
    tyroTransactionId: copy.tyroTransactionId || undefined,
  }));
  const firstCustomer = combinedCopies.find((copy) => copy.customerReceipt)?.customerReceipt || '';
  const firstMerchant = combinedCopies.find((copy) => copy.merchantReceipt)?.merchantReceipt || '';
  return combinedCopies.length
    ? {
        customerReceipt: firstCustomer,
        merchantReceipt: firstMerchant,
        copies: combinedCopies,
      }
    : null;
};

const ReturnsPage = () => {
  const receiptInputRef = useRef(null);
  const [receiptNumber, setReceiptNumber] = useState('');
  const [sale, setSale] = useState(null);
  const [previousRefunds, setPreviousRefunds] = useState([]);
  const [itemsToReturn, setItemsToReturn] = useState({});
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [refundTotal, setRefundTotal] = useState(0);
  const [isTyroRefundModalOpen, setTyroRefundModalOpen] = useState(false);
  const [tyroReceipts, setTyroReceipts] = useState(null);
  const [splitRefundByAllocation, setSplitRefundByAllocation] = useState({});
  const [pendingSplitRefundRequest, setPendingSplitRefundRequest] = useState(null);
  const [tyroSplitQueue, setTyroSplitQueue] = useState([]);
  const [activeTyroSplitLeg, setActiveTyroSplitLeg] = useState(null);
  const [processedTyroSplitCount, setProcessedTyroSplitCount] = useState(0);
  const [capturedTyroSplitReceipts, setCapturedTyroSplitReceipts] = useState({ copies: [] });
  const [recentRefund, setRecentRefund] = useState(null);
  const [showRefundReceipt, setShowRefundReceipt] = useState(false);
  const { activeStore } = useStore();
  const timezone = useStoreTimezone();

  const printStoredTyroReceipt = (receiptText, label) => {
    if (!receiptText) return;
    const printWindow = window.open('', '_blank', 'width=600,height=800');
    printWindow.document.write(`<pre>${receiptText}</pre>`);
    printWindow.document.title = label;
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const availableQuantities = useMemo(() => {
    if (!sale) return {};

    const refundedByLine = {};
    const remainingByLine = sale.items.map((item) => Number(item.quantity) || 0);
    const allocate = (lineIndex, quantity) => {
      const refundable = Math.max(remainingByLine[lineIndex] || 0, 0);
      const allocated = Math.min(refundable, quantity);
      if (allocated <= 0) return 0;
      remainingByLine[lineIndex] -= allocated;
      refundedByLine[lineIndex] = (refundedByLine[lineIndex] || 0) + allocated;
      return allocated;
    };

    for (const refund of previousRefunds) {
      for (const item of refund.refundedItems) {
        let qtyLeft = Math.max(Number(item.quantity) || 0, 0);
        if (qtyLeft <= 0) continue;

        const explicitIndex = parseLineIndex(item.saleItemIndex, sale.items.length);
        if (explicitIndex !== null) {
          qtyLeft -= allocate(explicitIndex, qtyLeft);
          if (qtyLeft <= 0) continue;
        }

        const refundedProductId = String(item.product || '');
        if (!refundedProductId) continue;

        const candidates = sale.items
          .map((saleItem, index) => ({ saleItem, index }))
          .filter(({ saleItem, index }) =>
            getSaleItemProductId(saleItem) === refundedProductId && (remainingByLine[index] || 0) > 0
          )
          .sort((a, b) => {
            const priceA = Math.abs(Number(a.saleItem.price || 0) - Number(item.price || 0));
            const priceB = Math.abs(Number(b.saleItem.price || 0) - Number(item.price || 0));
            const pricePenaltyA = priceA <= PRICE_MATCH_EPSILON ? 0 : 10;
            const pricePenaltyB = priceB <= PRICE_MATCH_EPSILON ? 0 : 10;
            const nameA = (a.saleItem.name || '').trim().toLowerCase();
            const nameB = (b.saleItem.name || '').trim().toLowerCase();
            const targetName = (item.name || '').trim().toLowerCase();
            const namePenaltyA = targetName && nameA !== targetName ? 1 : 0;
            const namePenaltyB = targetName && nameB !== targetName ? 1 : 0;
            const scoreA = pricePenaltyA + namePenaltyA + (pricePenaltyA === 0 ? priceA : 0);
            const scoreB = pricePenaltyB + namePenaltyB + (pricePenaltyB === 0 ? priceB : 0);
            return scoreA - scoreB || a.index - b.index;
          });

        for (const candidate of candidates) {
          qtyLeft -= allocate(candidate.index, qtyLeft);
          if (qtyLeft <= 0) break;
        }
      }
    }

    const available = {};
    for (let i = 0; i < sale.items.length; i += 1) {
      available[i] = Math.max((Number(sale.items[i].quantity) || 0) - (refundedByLine[i] || 0), 0);
    }
    return available;
  }, [sale, previousRefunds]);

  const originalPaymentAllocations = useMemo(
    () => normalizeSalePaymentAllocations(sale),
    [sale]
  );

  const refundedByAllocation = useMemo(
    () => buildRefundedAmountByAllocation(previousRefunds),
    [previousRefunds]
  );

  const allocationRefundAvailability = useMemo(() => {
    return originalPaymentAllocations.map((allocation) => {
      const alreadyRefunded = Number(refundedByAllocation[allocation.allocationId] || 0);
      return {
        ...allocation,
        alreadyRefunded: roundMoney(alreadyRefunded),
        availableToRefund: roundMoney(Math.max(Number(allocation.amount || 0) - alreadyRefunded, 0)),
      };
    });
  }, [originalPaymentAllocations, refundedByAllocation]);

  const isSplitOriginSale = originalPaymentAllocations.length > 1;
  const splitRefundCashOverrideEnabled = Boolean(activeStore?.splitRefundCashOverrideEnabled);
  const useSplitCashOverride = isSplitOriginSale && splitRefundCashOverrideEnabled;

  const selectedRefundAllocations = useMemo(() => {
    return allocationRefundAvailability
      .map((allocation) => {
        const requested = roundMoney(splitRefundByAllocation[allocation.allocationId] || 0);
        if (requested <= 0) return null;
        return {
          allocationId: allocation.allocationId,
          method: allocation.method,
          source: allocation.source,
          amount: Math.min(requested, allocation.availableToRefund),
        };
      })
      .filter(Boolean);
  }, [allocationRefundAvailability, splitRefundByAllocation]);

  const selectedRefundAllocationTotal = useMemo(
    () =>
      roundMoney(
        selectedRefundAllocations.reduce((sum, allocation) => sum + Number(allocation.amount || 0), 0)
      ),
    [selectedRefundAllocations]
  );

  useEffect(() => {
    const items = buildItemsToReturnPayload(sale, itemsToReturn);
    if (sale && items.length > 0) {
      refundsService.previewRefund({ saleId: sale._id, itemsToReturn: items })
        .then(data => setRefundTotal(data.refundAmount))
        .catch(() => setError('Could not calculate refund total.'));
    } else {
      setRefundTotal(0);
    }
  }, [itemsToReturn, sale]);

  const autoAllocateRefundByTender = useMemo(
    () => () => {
      let remaining = roundMoney(refundTotal);
      const next = {};
      allocationRefundAvailability.forEach((allocation) => {
        if (remaining <= 0) {
          next[allocation.allocationId] = 0;
          return;
        }
        const applied = Math.min(remaining, Number(allocation.availableToRefund || 0));
        next[allocation.allocationId] = roundMoney(applied);
        remaining = roundMoney(remaining - applied);
      });
      setSplitRefundByAllocation(next);
    },
    [allocationRefundAvailability, refundTotal]
  );

  useEffect(() => {
    if (!sale) {
      setSplitRefundByAllocation({});
      setPendingSplitRefundRequest(null);
      setTyroSplitQueue([]);
      setActiveTyroSplitLeg(null);
      setProcessedTyroSplitCount(0);
      setCapturedTyroSplitReceipts({ copies: [] });
      return;
    }
    if (isSplitOriginSale) {
      autoAllocateRefundByTender();
    } else {
      setSplitRefundByAllocation({});
    }
  }, [sale, isSplitOriginSale, autoAllocateRefundByTender]);

  useEffect(() => {
    // Focus receipt field when the page mounts to speed up scanning/typing
    if (receiptInputRef.current) {
      receiptInputRef.current.focus();
    }
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!activeStore) {
      setError('Please select a store.');
      return;
    }
    const normalizedInput = normalizeReceiptNumber(receiptNumber) || receiptNumber;
    if (normalizedInput && normalizedInput !== receiptNumber) {
      setReceiptNumber(normalizedInput);
    }
    setIsLoading(true);
    setError(''); setSuccess(''); setSale(null); setItemsToReturn({}); setPreviousRefunds([]); setTyroReceipts(null);
    setSplitRefundByAllocation({});
    setPendingSplitRefundRequest(null);
    setTyroSplitQueue([]);
    setActiveTyroSplitLeg(null);
    setProcessedTyroSplitCount(0);
    setCapturedTyroSplitReceipts({ copies: [] });
    setRecentRefund(null); setShowRefundReceipt(false);
    const candidates = getReceiptCandidates(normalizedInput || receiptNumber);
    let lastError = null;
    for (const candidate of candidates) {
      try {
        const foundSale = await refundsService.findSaleByReceipt(candidate, activeStore._id);
        const refundsData = await refundsService.getRefundsForSale(foundSale._id, activeStore._id);
        setSale(foundSale);
        setReceiptNumber(foundSale.receiptNumber || candidate);
        setPreviousRefunds(refundsData);
        setIsLoading(false);
        return;
      } catch (err) {
        lastError = err;
      }
    }
    setError(lastError?.response?.data?.message || 'Failed to find sale.');
    setIsLoading(false);
  };

  const handleQuantityChange = (lineIndex, availableToRefund, change) => {
    const currentQty = itemsToReturn[lineIndex] || 0;
    const newQty = currentQty + change;
    if (newQty >= 0 && newQty <= availableToRefund) {
      setItemsToReturn(prev => ({ ...prev, [lineIndex]: newQty }));
    }
  };

  const submitRefundRequest = async (returnData, fallbackReceipts = null) => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    const saleSnapshot = sale;
    try {
      const refund = await refundsService.processRefund(returnData);
      setSuccess(`Refund of $${refund.refundAmount.toFixed(2)} processed successfully.`);
      setRecentRefund({ refund, sale: saleSnapshot });
      setShowRefundReceipt(true);
      if (refund.tyroReceipts || fallbackReceipts) {
        setTyroReceipts(refund.tyroReceipts || fallbackReceipts);
      } else {
        setTyroReceipts(null);
      }
      setSale(null);
      setReceiptNumber('');
      setItemsToReturn({});
      setSplitRefundByAllocation({});
      setPendingSplitRefundRequest(null);
      setTyroSplitQueue([]);
      setActiveTyroSplitLeg(null);
      setProcessedTyroSplitCount(0);
      setCapturedTyroSplitReceipts({ copies: [] });
    } catch (err) {
      console.error('[ReturnsPage] Refund failed', err);
      setError(err.response?.data?.message || 'A server error occurred while processing the refund.');
      if (fallbackReceipts) {
        setTyroReceipts(fallbackReceipts);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleProcessRefund = async () => {
    if (!sale) return;
    const selectedItems = buildItemsToReturnPayload(sale, itemsToReturn);
    if (selectedItems.length === 0) {
      setError('Please select at least one item to return.');
      return;
    }

    const baseRefundData = {
      saleId: sale._id,
      reason,
      itemsToReturn: selectedItems,
      storeId: activeStore?._id,
    };

    let initialRequest = baseRefundData;
    if (isSplitOriginSale) {
      if (useSplitCashOverride) {
        initialRequest = {
          ...baseRefundData,
          refundAsCash: true,
        };
      } else {
        if (Math.abs(selectedRefundAllocationTotal - roundMoney(refundTotal)) > 0.01) {
          setError('Split refund allocations must match the refund total.');
          return;
        }
        if (!selectedRefundAllocations.length) {
          setError('Enter split refund allocations before processing.');
          return;
        }
        initialRequest = {
          ...baseRefundData,
          refundAllocations: selectedRefundAllocations.map((allocation) => ({ ...allocation })),
        };
      }
    } else if (originalPaymentAllocations[0]) {
      initialRequest = {
        ...baseRefundData,
        refundAllocations: [
          {
            allocationId: originalPaymentAllocations[0].allocationId,
            method: originalPaymentAllocations[0].method,
            source: originalPaymentAllocations[0].source,
            amount: roundMoney(refundTotal),
          },
        ],
      };
    }

    const tyroLegs = (initialRequest.refundAllocations || []).filter(isTyroAllocation);
    if (!tyroLegs.length) {
      await submitRefundRequest(initialRequest);
      return;
    }

    setPendingSplitRefundRequest(initialRequest);
    setTyroSplitQueue(tyroLegs.map((allocation) => allocation.allocationId));
    setActiveTyroSplitLeg(tyroLegs[0]);
    setCapturedTyroSplitReceipts({ copies: [] });
    setProcessedTyroSplitCount(0);
    setError('');
    setTyroRefundModalOpen(true);
  };

  const handleTyroRefundSuccess = async (transactionId, receipts) => {
    if (!activeTyroSplitLeg || !pendingSplitRefundRequest) {
      setTyroRefundModalOpen(false);
      return;
    }

    const updatedRequest = {
      ...pendingSplitRefundRequest,
      refundAllocations: (pendingSplitRefundRequest.refundAllocations || []).map((allocation) =>
        allocation.allocationId === activeTyroSplitLeg.allocationId
          ? { ...allocation, tyroTransactionId: transactionId || allocation.tyroTransactionId }
          : allocation
      ),
    };
    const updatedReceipts = mergeTyroReceipts(capturedTyroSplitReceipts, receipts, transactionId);
    const pendingQueue = tyroSplitQueue.filter((allocationId) => allocationId !== activeTyroSplitLeg.allocationId);
    const nextLeg =
      pendingQueue.length > 0
        ? (updatedRequest.refundAllocations || []).find((allocation) => allocation.allocationId === pendingQueue[0]) || null
        : null;

    if (nextLeg) {
      setPendingSplitRefundRequest(updatedRequest);
      setCapturedTyroSplitReceipts(updatedReceipts || { copies: [] });
      setTyroSplitQueue(pendingQueue);
      setActiveTyroSplitLeg(nextLeg);
      setProcessedTyroSplitCount((count) => count + 1);
      return;
    }

    setTyroRefundModalOpen(false);
    await submitRefundRequest(updatedRequest, updatedReceipts);
  };

  const handleTyroRefundCancelled = () => {
    setTyroRefundModalOpen(false);
    setPendingSplitRefundRequest(null);
    setTyroSplitQueue([]);
    setActiveTyroSplitLeg(null);
    setProcessedTyroSplitCount(0);
    setCapturedTyroSplitReceipts({ copies: [] });
  };

  const hasItemsToReturn = Object.values(itemsToReturn).some(qty => qty > 0);
  const latestTyroReceiptCopies = useMemo(() => resolveTyroReceiptCopies(tyroReceipts), [tyroReceipts]);
  const hasSplitAllocationMismatch =
    isSplitOriginSale &&
    !useSplitCashOverride &&
    Math.abs(selectedRefundAllocationTotal - roundMoney(refundTotal)) > 0.01;
  const requiresSplitAllocationEntry =
    isSplitOriginSale && !useSplitCashOverride;
  const canSubmitRefund =
    hasItemsToReturn &&
    !isLoading &&
    (!isSplitOriginSale || !requiresSplitAllocationEntry || (!hasSplitAllocationMismatch && selectedRefundAllocations.length > 0));
  const paymentSummaryLabel =
    originalPaymentAllocations.length > 1
      ? 'Split'
      : `${originalPaymentAllocations[0]?.method || sale?.paymentMethod || 'Card'}`;
  const activeTyroLegIndex = activeTyroSplitLeg
    ? (pendingSplitRefundRequest?.refundAllocations || [])
        .filter(isTyroAllocation)
        .findIndex((allocation) => allocation.allocationId === activeTyroSplitLeg.allocationId) + 1
    : 0;
  const activeTyroLegTotal = pendingSplitRefundRequest?.refundAllocations?.filter(isTyroAllocation)?.length || 0;
  const selectedTyroLegCount = selectedRefundAllocations.filter(isTyroAllocation).length;

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <CashierQuickActions />
      <h1 className="text-3xl font-bold text-gray-800 mb-6">
        Process a Return for: <span className="text-blue-600">{activeStore?.name}</span>
      </h1>
      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
      {success && <p className="bg-green-100 text-green-700 p-3 rounded mb-2">{success}</p>}
      {latestTyroReceiptCopies.length > 0 && (
        <div className="bg-white border border-green-200 rounded p-4 mb-4">
          <h3 className="text-lg font-semibold mb-2">Tyro EFTPOS Receipts</h3>
          <div className="space-y-3">
            {latestTyroReceiptCopies.map((copy, index) => (
              <div key={`latest-tyro-${index}`} className="bg-gray-100 rounded p-2">
                <p className="text-xs font-semibold text-gray-600 mb-1">
                  {`Split leg ${index + 1}${copy?.tyroTransactionId ? ` • Txn ${copy.tyroTransactionId}` : ''}`}
                </p>
                <pre className="text-xs max-h-40 overflow-auto whitespace-pre-wrap">
{`${copy.customerReceipt || ''}${copy.customerReceipt && copy.merchantReceipt ? '\n\n----- MERCHANT COPY -----\n\n' : ''}${copy.merchantReceipt || ''}`}
                </pre>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <form onSubmit={handleSearch} className="flex items-end space-x-4">
          <div className="flex-grow">
            <label htmlFor="receiptNumber" className="block text-sm font-medium text-gray-700">Receipt Number</label>
            <input
              ref={receiptInputRef}
              type="text"
              id="receiptNumber"
              value={receiptNumber}
              onChange={(e) => setReceiptNumber(e.target.value)}
              className="mt-1 w-full border-gray-300 rounded-md shadow-sm"
              placeholder="e.g., 000001"
              required
            />
          </div>
          <button type="submit" disabled={isLoading || !activeStore} className="bg-primary text-white px-4 py-2 rounded-md flex items-center disabled:bg-gray-400">
            <FiSearch className="mr-2" /> {isLoading ? 'Searching...' : 'Find Sale'}
          </button>
        </form>
      </div>
      
      {sale && (
        <div className="bg-white p-4 rounded-lg shadow-md">
          <h2 className="text-xl font-bold mb-2">Original Sale Details</h2>
          <p><strong>Date:</strong> {formatInStoreTimezone(sale.createdAt, 'dd-MM-yyyy hh:mm a', timezone)}</p>
          <p className="mb-4"><strong>Total Paid:</strong> ${sale.finalAmount.toFixed(2)}</p>
          <p className="mb-2"><strong>Payment:</strong> {paymentSummaryLabel}</p>
          
          <h3 className="text-lg font-semibold mt-4 border-t pt-4">Select Items to Return</h3>
          <div className="space-y-3 mt-2">
            {sale.items.map((item, lineIndex) => {
              const effectivePricePerItem = getEffectivePricePerItem(item, sale);
              const availableToRefund = availableQuantities[lineIndex] || 0;
              const selectedQty = itemsToReturn[lineIndex] || 0;
              const lineRefundTotal = selectedQty * effectivePricePerItem;

              return (
                <div key={`${getSaleItemProductId(item)}-${lineIndex}`} className={`grid grid-cols-4 items-center gap-4 p-2 border rounded-md ${availableToRefund === 0 ? 'bg-gray-100 opacity-60' : ''}`}>
                  <div className="col-span-2">
                    <p className="font-semibold">{item.name}</p>
                    <p className="text-sm text-gray-500">Purchased: {item.quantity}</p>
                    <p className="text-sm font-bold text-accent-green">Available to Refund: {availableToRefund}</p>
                  </div>
                  <div className="flex items-center justify-center">
                    <button onClick={() => handleQuantityChange(lineIndex, availableToRefund, -1)} disabled={availableToRefund === 0} className="p-2 rounded-full hover:bg-gray-200 disabled:opacity-50"><FiMinus /></button>
                    <span className="mx-3 text-lg font-bold">{selectedQty}</span>
                    <button onClick={() => handleQuantityChange(lineIndex, availableToRefund, 1)} disabled={availableToRefund === 0} className="p-2 rounded-full hover:bg-gray-200 disabled:opacity-50"><FiPlus /></button>
                  </div>
                  <div className="text-right font-semibold">${lineRefundTotal.toFixed(2)}</div>
                </div>
              );
            })}
          </div>

          {isSplitOriginSale && (
            <div className="mt-6 bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
              {useSplitCashOverride ? (
                <>
                  <div>
                    <h3 className="text-lg font-semibold">Cash Refund</h3>
                    <p className="text-sm text-slate-600">
                      Split refund-as-cash is enabled for this store.
                    </p>
                  </div>
                  <div className="bg-white border rounded p-3 flex items-center justify-between">
                    <span className="text-sm text-slate-600">Amount to refund by cash</span>
                    <span className="text-lg font-semibold text-slate-900">${refundTotal.toFixed(2)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold">Split Refund Allocation</h3>
                      <p className="text-sm text-slate-600">Allocate this refund back to original tenders.</p>
                    </div>
                    <button
                      type="button"
                      onClick={autoAllocateRefundByTender}
                      className="px-3 py-1.5 text-sm border border-primary text-primary rounded hover:bg-primary hover:text-white"
                    >
                      Auto Allocate
                    </button>
                  </div>
                  <div className="space-y-2">
                    {allocationRefundAvailability.map((allocation, index) => (
                      <div key={allocation.allocationId} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-center bg-white border rounded p-2">
                        <div className="md:col-span-2">
                          <p className="font-semibold text-sm">
                            {`Leg ${index + 1}: ${allocation.method}`}
                            {allocation?.source ? ` (${allocation.source})` : ''}
                          </p>
                          <p className="text-xs text-gray-500">{`Allocation ID: ${allocation.allocationId}`}</p>
                        </div>
                        <p className="text-xs md:text-sm">{`Original: $${roundMoney(allocation.amount).toFixed(2)}`}</p>
                        <p className="text-xs md:text-sm">{`Refunded: $${roundMoney(allocation.alreadyRefunded).toFixed(2)}`}</p>
                        <p className="text-xs md:text-sm font-semibold">{`Available: $${roundMoney(allocation.availableToRefund).toFixed(2)}`}</p>
                        <input
                          type="number"
                          min="0"
                          max={allocation.availableToRefund}
                          step="0.01"
                          value={splitRefundByAllocation[allocation.allocationId] || 0}
                          onChange={(event) => {
                            const requested = roundMoney(event.target.value);
                            setSplitRefundByAllocation((prev) => ({
                              ...prev,
                              [allocation.allocationId]: Math.min(
                                Math.max(Number.isFinite(requested) ? requested : 0, 0),
                                allocation.availableToRefund
                              ),
                            }));
                          }}
                          className="border-gray-300 rounded-md shadow-sm text-right"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap justify-between items-center border-t pt-2">
                    <p className="text-sm font-semibold">
                      Allocated: ${selectedRefundAllocationTotal.toFixed(2)} / ${refundTotal.toFixed(2)}
                    </p>
                    {hasSplitAllocationMismatch ? (
                      <p className="text-xs text-red-600">Allocation total must match refund total.</p>
                    ) : (
                      <p className="text-xs text-emerald-700">Allocation total is valid.</p>
                    )}
                  </div>
                  {selectedTyroLegCount > 1 && (
                    <p className="text-xs text-blue-700">
                      {`Card refunds will be processed one-by-one on Tyro (${selectedTyroLegCount} separate card legs).`}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {previousRefunds.length > 0 && (
            <div className="mt-6 bg-gray-50 border rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-3">Previous Refunds</h3>
              <div className="space-y-3">
                {previousRefunds.map((refund) => {
                  const refundTyroCopies = resolveTyroReceiptCopies(refund.tyroReceipts);
                  return (
                  <div key={refund._id} className="p-3 border rounded-md bg-white">
                    <div className="flex flex-wrap justify-between text-sm">
                      <div>
                        <p className="font-semibold">Refund #{refund._id.slice(-6)}</p>
                        <p className="text-gray-500">
                          {formatInStoreTimezone(refund.createdAt, 'dd-MM-yyyy hh:mm a', timezone)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">${refund.refundAmount.toFixed(2)}</p>
                        {refund.tyroTransactionId && (
                          <p className="text-xs text-gray-500">Tyro Txn: {refund.tyroTransactionId}</p>
                        )}
                      </div>
                    </div>
                    {refundTyroCopies.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {refundTyroCopies.map((copy, index) => (
                          <div key={`${refund._id}-copy-${index}`} className="flex flex-wrap gap-2">
                            {copy.customerReceipt && (
                              <button
                                type="button"
                                className="px-3 py-1 border rounded text-sm text-blue-600 border-blue-600 hover:bg-blue-600 hover:text-white"
                                onClick={() => printStoredTyroReceipt(copy.customerReceipt, `Customer Refund Receipt - Leg ${index + 1}`)}
                              >
                                {`Print Customer Copy (Leg ${index + 1})`}
                              </button>
                            )}
                            {copy.merchantReceipt && (
                              <button
                                type="button"
                                className="px-3 py-1 border rounded text-sm text-blue-600 border-blue-600 hover:bg-blue-600 hover:text-white"
                                onClick={() => printStoredTyroReceipt(copy.merchantReceipt, `Merchant Refund Receipt - Leg ${index + 1}`)}
                              >
                                {`Print Merchant Copy (Leg ${index + 1})`}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-6">
            <label htmlFor="reason" className="block text-sm font-medium text-gray-700">Reason for Return (Optional)</label>
            <input type="text" id="reason" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full border-gray-300 rounded-md shadow-sm" />
          </div>
          <div className="mt-6 border-t pt-4 flex justify-between items-center">
            <div className="text-2xl font-bold">Refund Total: <span className="text-blue-600">${refundTotal.toFixed(2)}</span></div>
            <div>
              <button
                type="button"
                onClick={() => handleProcessRefund()}
                disabled={!canSubmitRefund}
                className="bg-primary text-white px-6 py-3 rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Processing...' : 'Process Refund'}
              </button>
            </div>
          </div>
        </div>
      )}
      {isTyroRefundModalOpen && activeTyroSplitLeg && (
        <TyroRefundModal
          total={roundMoney(activeTyroSplitLeg.amount || 0)}
          title={
            activeTyroLegTotal > 1
              ? `Process Tyro Refund (Leg ${activeTyroLegIndex}/${activeTyroLegTotal})`
              : 'Process Tyro Refund'
          }
          subtitle={
            activeTyroLegTotal > 1
              ? 'Complete each Tyro leg to finish this split refund.'
              : undefined
          }
          disableCancel={processedTyroSplitCount > 0}
          onCancel={handleTyroRefundCancelled}
          onRefundSuccess={handleTyroRefundSuccess}
          onRefundError={(err) => {
            setError(err || 'Tyro refund failed. Please retry this leg.');
          }}
        />
      )}
      {showRefundReceipt && recentRefund && (
        <RefundReceiptModal
          refund={recentRefund.refund}
          originalSale={recentRefund.sale}
          store={activeStore}
          onClose={() => {
            setShowRefundReceipt(false);
            setRecentRefund(null);
          }}
        />
      )}
    </div>
  );
};

export default ReturnsPage;
